// EditActivityForm.js

import { Button, Form, Input, Modal, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import {
	ACTIVITY_TYPE_MAPPING,
	ACTIVITY_TYPE_OPTIONS,
} from "../utils/activityTypes";

// Issue #82: タイトルが「Edit Activity」固定だったため、管理者が複数人の記録を
// 続けて直すとき「誰のいつの記録を開いているか」が画面から分からなかった。
// 対象者と日付をタイトルへ出す。
//
// 氏名・日付は selectedActivity（HomePage の一覧 record）由来で、呼び出し元に
// よっては欠けることがあるため、無い項目は落として組み立てる。
export const buildEditActivityTitle = (activity) => {
	if (!activity) {
		return "Edit Activity";
	}

	const name = [activity.lastName, activity.firstName]
		.filter(Boolean)
		.join(", ");
	const date = activity.activityDate;

	if (name && date) {
		return `Edit Activity — ${name} (${date})`;
	}
	if (name) {
		return `Edit Activity — ${name}`;
	}
	if (date) {
		return `Edit Activity (${date})`;
	}
	return "Edit Activity";
};

const EditActivityForm = ({
	open,
	onUpdate,
	onCancel,
	selectedActivity,
	onDelete,
}) => {
	const [form] = Form.useForm();
	// Issue #88: 開いた直後に最初の入力欄（Activity 本文）へフォーカスする。
	// このモーダルは destroyOnClose を持たず一度開くと mount され続けるため、
	// autoFocus 属性では 2 回目以降の表示で効かない。
	const activityTextRef = useRef(null);
	// Issue #69: 更新・削除とも実行中は操作を受け付けない。押せたままだと、
	// レスポンスを待つ間の連打で同じ更新・削除が何度も飛ぶ。
	//
	// state だけで判定すると反映が次のレンダー以降になり、同じティックで続けて
	// 押された分を弾けない（validateFields が非同期のため、先行クリックが
	// setUpdating へ到達する前に後続が入る）。押された瞬間に同期で閉じる ref を
	// ガードにし、state はボタンの見た目（loading / disabled）専用にする。
	const busyRef = useRef(false);
	const [updating, setUpdating] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const busy = updating || deleting;

	const handleDelete = () => {
		if (busyRef.current) return;
		Modal.confirm({
			title: "Delete this activity?",
			content: `This will permanently delete the activity on ${selectedActivity.activityDate}.`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			// Promise を返すと antd が確認ダイアログの OK を実行中 loading にする
			onOk: async () => {
				busyRef.current = true;
				setDeleting(true);
				try {
					await onDelete(selectedActivity.id);
					form.resetFields();
				} finally {
					busyRef.current = false;
					setDeleting(false);
				}
			},
		});
	};

	useEffect(() => {
		if (open && selectedActivity) {
			form.setFieldsValue({
				userActivity: selectedActivity.userActivity,
				activityType: ACTIVITY_TYPE_MAPPING[selectedActivity.activityType],
				activityDate: new Date(selectedActivity.activityDate),
			});
		}
	}, [open, selectedActivity, form]);

	useEffect(() => {
		if (!open) return undefined;
		const timer = setTimeout(() => activityTextRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [open]);

	const handleUpdate = () => {
		if (busyRef.current) return;
		busyRef.current = true;
		setUpdating(true);
		form
			.validateFields()
			.then(async (values) => {
				const { userActivity, activityType } = values;

				await onUpdate(selectedActivity.id, {
					activityType: activityType, // Use activityType directly from values
					userActivity,
				});
				form.resetFields();
			})
			.catch((info) => {
				console.log("Validate Failed:", info);
			})
			.finally(() => {
				busyRef.current = false;
				setUpdating(false);
			});
	};

	return (
		<Modal
			open={open}
			title={buildEditActivityTitle(selectedActivity)}
			// Issue #82: Create 系は okText を指定済みだが Edit だけ既定の「OK」のままで、
			// 何が起きるボタンなのか分からなかった。
			okText="Update"
			onOk={handleUpdate}
			confirmLoading={updating}
			okButtonProps={{ disabled: deleting }}
			cancelButtonProps={{ disabled: busy }}
			closable={!busy}
			maskClosable={!busy}
			onCancel={() => {
				form.resetFields();
				onCancel();
			}}
		>
			<Form form={form} layout="vertical">
				<Form.Item name="userActivity" label="Activity">
					<Input.TextArea ref={activityTextRef} rows={4} />
				</Form.Item>

				<Form.Item
					name="activityType"
					label="Activity Type"
					rules={[
						{ required: true, message: "Please select an activity type." },
					]}
				>
					{/* Issue #82: 選択肢の定義は utils/activityTypes.js に集約し、
					    Create 系フォームと同じラベルを使う */}
					<Select options={ACTIVITY_TYPE_OPTIONS} />
				</Form.Item>

				<Form.Item>
					<Button
						type="primary"
						onClick={handleDelete}
						loading={deleting}
						disabled={updating}
						style={{ color: "white", width: "100%", textAlign: "center" }}
						danger
					>
						Delete Activity
					</Button>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default EditActivityForm;
